/**
 * CreateDashboardModal – Refactored to dashboard-only flow.
 *
 * Fields:
 *  - Role (required) — from GraphQL reference_user_role
 *  - Dashboard (required) — only resourceType === "dashboard" resources
 *  - Display Name (optional) — auto-filled from dashboard name if empty
 *  - Set as Default (checkbox)
 *
 * On dashboard select → fetches dashboard details (questions + filters) as read-only preview.
 * Save disabled until role + dashboard are selected.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { RoleSelector } from '@/v2/pages/admin/dashboard-config/components/RoleSelector';
import { DashboardSelector } from '@/v2/pages/admin/dashboard-config/components/DashboardSelector';
import { DashboardDetailsPreview } from '@/v2/pages/admin/dashboard-config/components/DashboardDetailsPreview';
import {
  useUserRoles,
  useMetabaseDashboards,
  useDashboardDetails,
  useCreateDashboardItem,
} from '@/hooks/dashboardConfig/useDashboardConfig';
import type { MetabaseFlatResource } from '@/types/dashboardConfig';

interface CreateDashboardModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateDashboardModal = ({ open, onClose, onSuccess }: CreateDashboardModalProps) => {
  const { user } = useAuth();
  const loginUserId = user?.id ?? null;

  // ─── Form state ─────────────────────────────────────────────────────────────
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);
  const [selectedDashboardKey, setSelectedDashboardKey] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<MetabaseFlatResource | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [loadDetailsDashboardId, setLoadDetailsDashboardId] = useState<number | null>(null);

  // ─── Data hooks ─────────────────────────────────────────────────────────────
  const { roles, isLoading: rolesLoading } = useUserRoles();
  const { dashboards, isLoading: dashboardsLoading } = useMetabaseDashboards(loginUserId);
  const {
    details,
    isLoading: detailsLoading,
    error: detailsError,
    retry,
  } = useDashboardDetails(
    loginUserId,
    loadDetailsDashboardId
  );
  const { create, isLoading: creating } = useCreateDashboardItem(loginUserId);

  // ─── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setSelectedRole('');
      setSelectedRoleId(0);
      setSelectedDashboardKey('');
      setSelectedDashboard(null);
      setDisplayName('');
      setIsDefault(false);
      setLoadDetailsDashboardId(null);
    }
  }, [open]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleDashboardChange = (key: string, resource: MetabaseFlatResource) => {
    setSelectedDashboardKey(key);
    setSelectedDashboard(resource);
    setDisplayName('');
    setLoadDetailsDashboardId(null); // reset preview on new selection
  };

  const handleLoadDetails = () => {
    if (selectedDashboard) {
      setLoadDetailsDashboardId(selectedDashboard.resourceId);
    }
  };

  const canSave =
    selectedRole.trim() !== '' &&
    selectedDashboardKey.trim() !== '' &&
    !creating;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || !selectedDashboard) return;

    const resolvedName = displayName.trim() || selectedDashboard.displayName;

    const payload = {
      customName: resolvedName,
      metabaseQuestionName: selectedDashboard.displayName,
      resourceType: 'dashboard' as const,
      resourceId: selectedDashboard.resourceId,
      userTypes: [selectedRole],
      userRoleIds: [selectedRoleId],
      dashboardQuestions: details?.questions ?? [],
      dashboardFilters: details?.filters ?? [],
      isDefault,
    };

    const result = await create(payload);

    if (result.success) {
      toast({
        title: 'Dashboard created',
        description: `"${resolvedName}" has been created for role ${selectedRole}.`,
      });
      onSuccess();
      onClose();
    } else {
      toast({
        title: 'Failed to create dashboard',
        description: result.error ?? 'Unknown error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">Create Custom Dashboard</DialogTitle>
          <DialogDescription>
            Select a role and a Metabase dashboard to create a custom dashboard configuration.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 py-2">
          {/* Role */}
          <RoleSelector
            roles={roles}
            isLoading={rolesLoading}
            value={selectedRole}
            onChange={(role, roleId) => {
              setSelectedRole(role);
              setSelectedRoleId(roleId);
            }}
          />

          {/* Dashboard */}
          <DashboardSelector
            dashboards={dashboards}
            isLoading={dashboardsLoading}
            value={selectedDashboardKey}
            onChange={handleDashboardChange}
          />

          {/* Load Details button — appears after dashboard selection, before preview */}
          {selectedDashboard && loadDetailsDashboardId !== selectedDashboard.resourceId && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleLoadDetails}
              disabled={detailsLoading}
              className="w-full"
            >
              {detailsLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading Details…
                </>
              ) : (
                'Load Dashboard Details'
              )}
            </Button>
          )}

          {/* Dashboard Details Preview (read-only) — only after load */}
          {selectedDashboard && loadDetailsDashboardId === selectedDashboard.resourceId && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-muted-foreground font-medium">
                  Dashboard Preview
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs h-auto py-0.5 px-2"
                  onClick={() => setLoadDetailsDashboardId(null)}
                >
                  Hide
                </Button>
              </div>
              <DashboardDetailsPreview
                details={details}
                isLoading={detailsLoading}
                error={detailsError}
                onRetry={retry}
              />
            </div>
          )}

          {/* Display Name (optional) */}
          <div className="space-y-1.5">
            <Label htmlFor="displayName" className="text-sm font-medium text-foreground">
              Display Name
            </Label>
            <Input
              id="displayName"
              placeholder={
                selectedDashboard
                  ? `Default: "${selectedDashboard.displayName}"`
                  : 'e.g. ES Monthly Overview'
              }
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          {/* Set as Default */}
          <div className="flex items-center gap-3">
            <Switch id="isDefault" checked={isDefault} onCheckedChange={setIsDefault} />
            <Label htmlFor="isDefault" className="text-sm text-foreground cursor-pointer">
              Set as default dashboard for this role
            </Label>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {creating ? 'Saving...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDashboardModal;
